import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";

const money = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN")}`);
const QC = ["QC PENDING", "QC PASSED", "QC REVIEW", "QC FAILED"];
const MATS = ["ORDERED", "PREPARING", "READY", "DISPATCHED", "RECEIVED", "READY_FOR_INSTALLATION"];

export default function AdminApplicatorJobs() {
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("");
  const [rules, setRules] = useState(null);
  const [alloc, setAlloc] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const [eligible, setEligible] = useState([]);

  const load = () => api.get("/admin/applicator-jobs", { params: filter ? { status: filter } : {} }).then(({ data }) => setJobs(data)).catch(() => {});
  useEffect(() => { load(); }, [filter]);
  useEffect(() => { api.get("/admin/commission-rules").then(({ data }) => setRules(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/admin/allocation-settings").then(({ data }) => setAlloc(data.value)).catch(() => {}); }, []);

  const openAssign = async (j) => {
    setAssignFor(j.id);
    const { data } = await api.get(`/applicator/jobs/${j.id}/refer-eligible`).catch(() => ({ data: [] }));
    setEligible(data || []);
  };
  const assign = async (jid, aid) => { await api.put(`/admin/applicator-jobs/${jid}/assign`, { applicator_id: aid }); toast.success("Assigned"); setAssignFor(null); load(); };
  const setQc = async (jid, status) => { await api.put(`/admin/applicator-jobs/${jid}/qc`, { status }); toast.success("QC updated"); load(); };
  const setMat = async (jid, status) => { const r = await api.put(`/admin/applicator-jobs/${jid}/material`, { status }); toast.success(r.data.offers_released ? "Material updated · job released to applicators" : "Material status updated"); load(); };
  const cancel = async (jid) => { const r = await api.put(`/admin/applicator-jobs/${jid}/cancel`); toast.success(`Cancelled · ${r.data.reversed_earnings} earnings reversed`); load(); };
  const saveRules = async () => { await api.put("/admin/commission-rules", rules); toast.success("Commission rules saved"); };
  const saveAlloc = async (next) => { setAlloc(next); await api.put("/admin/allocation-settings", next); toast.success("Allocation trigger saved"); load(); };

  return (
    <div className="min-h-screen bg-[#080808]" data-testid="admin-applicator-jobs-page">
      <Seo title="Admin · Applicator Jobs" description="Manage applicator jobs" />
      <div className="glass"><div className="max-w-[1200px] mx-auto px-5 h-[60px] flex items-center justify-between">
        <Link to="/admin/applicators" className="flex items-center gap-2 text-sm text-gray-300"><ArrowLeft size={16} /> Applicators</Link>
        <Link to="/admin/applicator-payouts" className="text-sm text-[#C9A45C]">Payouts →</Link>
      </div></div>

      <div className="max-w-[1200px] mx-auto px-5 py-10">
        <h1 className="serif-head text-3xl lg:text-4xl mb-6">Applicator Jobs</h1>

        {rules && (
          <div className="hair-border bg-white/[0.02] p-4 mb-8" data-testid="commission-rules">
            <p className="errah-label gold-text mb-3">Network commission rules (%)</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
              {[["direct_referral_pct", "Direct"], ["level_1_pct", "L1"], ["level_2_pct", "L2"], ["level_3_pct", "L3"], ["max_levels", "Max lvls"], ["min_completed_jobs", "Min jobs"], ["min_quality_score", "Min QC"]].map(([k, l]) => (
                <div key={k}><label className="errah-label mb-1 block">{l}</label>
                  <input type="number" value={rules[k] ?? 0} onChange={(e) => setRules({ ...rules, [k]: Number(e.target.value) })} data-testid={`rule-${k}`} className="w-full bg-transparent border border-white/15 px-2 py-1.5 text-sm" /></div>
              ))}
            </div>
            <button onClick={saveRules} data-testid="save-rules" className="mt-3 bg-[#C9A45C] text-black px-5 py-2 text-sm font-semibold">Save rules</button>
          </div>
        )}

        {alloc && (
          <div className="hair-border bg-white/[0.02] p-4 mb-6" data-testid="allocation-settings">
            <p className="errah-label gold-text mb-3">Material-first allocation trigger</p>
            <div className="flex flex-wrap gap-2">
              {[["on_material_dispatched", "Offer after material dispatched (recommended)"], ["immediate", "Offer immediately on booking"]].map(([v, l]) => (
                <button key={v} onClick={() => saveAlloc({ ...alloc, trigger: v })} data-testid={`alloc-${v}`}
                  className={`px-3 py-1.5 text-xs border ${alloc.trigger === v ? "border-[#C9A45C] text-[#C9A45C]" : "border-white/15 text-gray-400"}`}>{l}</button>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-5" data-testid="job-filter">
          {["", "awaiting_material", "open", "unassigned", "assigned", "in_progress", "completed", "cancelled"].map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 text-xs border ${filter === s ? "border-[#C9A45C] text-[#C9A45C]" : "border-white/15 text-gray-400"}`}>{s || "all"}</button>
          ))}
        </div>

        <div className="space-y-3">
          {jobs.map((j) => (
            <div key={j.id} className="hair-border bg-white/[0.02] p-4" data-testid={`admin-job-${j.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-display text-lg">{j.service}</p>
                  <p className="text-sm text-gray-400">{j.customer_name || "Customer"} · {j.city || "—"} · {money(j.estimated_value)}</p>
                  <p className="text-xs text-gray-500 mt-1">{j.id.slice(0, 14)} · {j.status} · QC {j.qc_status || "—"} · eligible {(j.eligible_applicator_ids || []).length}{j.booking_id ? " · from booking" : ""}</p>
                  <p className="text-xs mt-1"><span className="text-gray-500">Material:</span> <span className="gold-text">{(j.material_status || "ORDERED").replace(/_/g, " ")}</span></p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <select value={j.material_status || "ORDERED"} onChange={(e) => setMat(j.id, e.target.value)} data-testid={`admin-material-${j.id}`} className="bg-[#111] border border-[#C9A45C]/40 px-2 py-1.5 text-xs text-[#C9A45C]">{MATS.map((m) => <option key={m} value={m}>{m.replace(/_/g, " ")}</option>)}</select>
                  <button onClick={() => openAssign(j)} className="text-xs border border-[#C9A45C]/50 text-[#C9A45C] px-3 py-1.5">Assign</button>
                  <select value={j.qc_status || "QC PENDING"} onChange={(e) => setQc(j.id, e.target.value)} data-testid={`admin-qc-${j.id}`} className="bg-[#111] border border-white/15 px-2 py-1.5 text-xs">{QC.map((q) => <option key={q}>{q}</option>)}</select>
                  {j.status !== "cancelled" && <button onClick={() => cancel(j.id)} data-testid={`cancel-job-${j.id}`} className="text-xs border border-red-500/50 text-red-400 px-3 py-1.5">Cancel</button>}
                </div>
              </div>
              {assignFor === j.id && (
                <div className="mt-3 pt-3 border-t border-white/10" data-testid="assign-panel">
                  {eligible.length === 0 ? <p className="text-xs text-gray-500">No certified applicators found for this service/area.</p> : eligible.map((a) => (
                    <div key={a.applicator_id} className="flex items-center justify-between py-1.5">
                      <span className="text-sm">{a.name} <span className="text-xs gold-text">{a.applicator_code}</span> <span className="text-xs text-gray-500">{a.city}</span></span>
                      <button onClick={() => assign(j.id, a.applicator_id)} data-testid={`assign-to-${a.applicator_code}`} className="text-xs bg-[#C9A45C] text-black px-3 py-1.5 font-semibold">Assign</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {!jobs.length && <p className="text-sm text-gray-600">No jobs.</p>}
        </div>
      </div>
    </div>
  );
}
